import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { fontFaces } from '../../assets/fonts/fonts';
import moment from 'moment';
import logoImage from './raza-cover-letter-logo.png';

/**
 * Generates the header CSS styles
 * @returns {string} - CSS styles for the header
 */
const getHeaderStyles = () => {
  return `
    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      padding-bottom: 10px;
    }
    .logo-section {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .logo {
      width: 50px;
      height: 50px;
      background: #2d8659;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 20px;
    }
    .company-name {
      font-size: 18px;
      font-weight: bold;
      color: #2d8659;
    }
    .company-subtitle {
      font-size: 10px;
      color: #666;
    }
    .contact-info {
      text-align: right;
      font-size: 9px;
      line-height: 1.4;
      color: #333;
    }
    .contact-info div {
      margin-bottom: 2px;
    }
  `;
};

/**
 * Generates the header HTML
 * @param {string} logoDataUrl - Optional data URL for the logo image
 * @returns {string} - HTML for the header
 */
const getHeaderHTML = (logoDataUrl = '', ticketData = null) => {
  // Use imported logo image (webpack will handle the path)
  const logoImageUrl = logoImage;
  
  return `
    <div class="header-container">
      <div class="logo-section">
        ${logoDataUrl 
          ? `<img src="${logoDataUrl}" alt="RAZA Environmental" style="width: 80px; height: auto;" />`
          : logoImageUrl
          ? `<img src="${logoImageUrl}" alt="RAZA Environmental" style="width: 80px; height: auto;" crossorigin="anonymous" />`
          : '<div style="width: 180px; height: 60px; background: #f5f5f5; border: 1px solid #ddd; display: flex; align-items: center; justify-content: center; color: #999; font-size: 12px;">No logo available</div>'
        }
      </div>
      <div class="contact-info">
        <div>${ticketData?.company_name }</div>
        <div>${ticketData?.company_address }</div>
        <div>${ticketData?.company_phone }</div>
        <div>${ticketData?.company_email }</div>
      </div>
    </div>
  `;
};

/**
 * Generates a Mold Assessment and Remediation Work Plan PDF report
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 * @param {string} moldType - Type of mold inspection ('pre' or 'post')
 * @returns {Promise<jsPDF>} - PDF document
 */
export const generateMoldAssessmentPDF = async (data, buildingData, apartmentNumber, moldType = 'pre', inspectorCertificate, inspectorLicense, ticketData = null) => {
  if (!data) {
    throw new Error("Missing required data for PDF generation");
  }

  // Preload and convert logo image to data URL for all pages
  let logoDataUrl = '';
  // Use imported logo image (webpack will handle the path)
  const logoImageUrl = logoImage;
  
  console.log('Loading logo image from:', logoImageUrl);
  
  try {
    // First try to convert to data URL (best for PDF generation - no CORS issues)
    logoDataUrl = await convertImageToDataURL(logoImageUrl);
    
    if (logoDataUrl && logoDataUrl.length > 0) {
      console.log('Logo successfully converted to data URL, length:', logoDataUrl.length);
    } else {
      console.warn('Logo conversion returned empty, retrying with longer timeout...');
      // Retry with a longer timeout
      try {
        logoDataUrl = await convertImageToDataURL(logoImageUrl);
        if (logoDataUrl && logoDataUrl.length > 0) {
          console.log('Logo successfully converted to data URL on retry, length:', logoDataUrl.length);
        } else {
          console.warn('Logo conversion still failed, will preload and use URL directly');
          // If conversion still fails, preload the image
          try {
            await preloadImage(logoImageUrl);
            console.log('Logo image preloaded successfully');
          } catch (preloadError) {
            console.warn('Failed to preload logo image:', preloadError);
          }
        }
      } catch (retryError) {
        console.warn('Logo conversion retry also failed:', retryError);
        // If retry fails, at least preload the image
        try {
          await preloadImage(logoImageUrl);
          console.log('Logo image preloaded successfully (fallback)');
        } catch (preloadError) {
          console.warn('Failed to preload logo image:', preloadError);
        }
      }
    }
  } catch (error) {
    console.warn('Failed to convert logo to data URL, will preload and use URL directly:', error);
    // If conversion fails, at least preload the image so it's available
    try {
      await preloadImage(logoImageUrl);
      console.log('Logo image preloaded successfully (fallback)');
    } catch (preloadError) {
      console.warn('Failed to preload logo image:', preloadError);
    }
  }
  
  // Ensure we have a valid logoDataUrl or fallback to URL
  if (!logoDataUrl || logoDataUrl.length === 0) {
    console.warn('No logo data URL available, will use URL with CORS handling');
    logoDataUrl = ''; // Empty string will trigger URL fallback in getHeaderHTML
  }

  // Create PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Create the first page with header and report details
  await createMoldAssessmentPage(doc, data, buildingData, apartmentNumber, logoDataUrl, ticketData, moldType,inspectorCertificate);

  // Add breakdown process page - only for 'post' type (2nd page)
  // if (moldType === 'post') {
  //   doc.addPage();
  //   await createBreakdownProcessPage(doc, logoDataUrl, ticketData);
  // }
    // Add fourth page with assessment details
    doc.addPage();
    await createAssessmentDetailsPage(doc, data, buildingData, apartmentNumber, logoDataUrl, ticketData, moldType);



  // Add remediation work plan page - only for 'pre' type
  if (moldType === 'pre') {
    doc.addPage();
    await createRemediationWorkPlanPage(doc, data, buildingData, apartmentNumber, logoDataUrl, ticketData);
  }





  // Add fifth page with conclusion - only for 'pre' type



  // Add seventh page with measurement images
  doc.addPage();
  await createMeasurementImagesPage(doc, data, buildingData, apartmentNumber, logoDataUrl, ticketData);

  // Add room-specific images pages (one page per room) - only for 'pre' type
  if (moldType === 'pre') {
    const rooms = data?.rooms || [];
    if (rooms.length > 0) {
      for (const room of rooms) {
        doc.addPage();
        await createRoomImagesPage(doc, room, data, buildingData, apartmentNumber, logoDataUrl, ticketData);
      }
    }
  }

  // Add disclaimer page after room images
  doc.addPage();
  await createDisclaimerPage(doc, logoDataUrl, ticketData);

    // Add sixth page with limited evaluation terms
    doc.addPage();
    await createLimitedEvaluationPage(doc, data, buildingData, apartmentNumber, logoDataUrl, ticketData);
  

  // Add inspector certificate page - only for 'pre' type
  if (moldType === 'pre' && inspectorCertificate?.attachment_url) {
    doc.addPage();
    await createInspectorCertificatePage(doc, inspectorCertificate, logoDataUrl, ticketData);
  }

  // Add inspector license page - only for 'pre' type
  if (moldType === 'pre' && inspectorLicense?.attachment_url) {
    doc.addPage();
    await createInspectorLicensePage(doc, inspectorLicense, logoDataUrl, ticketData);
  }

  return doc;
};

/**
 * Creates the Mold Assessment page from HTML template
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 * @param {string} logoDataUrl - Data URL for the logo image
 * @param {Object} ticketData - Ticket data containing inspector_name
 * @param {string} moldType - Type of mold inspection ('pre' or 'post')
 */
const createMoldAssessmentPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null, moldType = 'pre',inspectorCertificate) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  
  // Get inspector name from data, ticket data, or fallback
  const getInspectorName = () => {
    return data?.prepared_by_name || ticketData?.inspector_name || 'Zamo Kucevic';
  };

  // Get property address
  const getPropertyAddress = () => {
    if (data?.property_address) return data.property_address;
    if (buildingData?.address) {
      const aptPart = apartmentNumber ? `, Apt #${apartmentNumber}` : '';
      const cityStateZip = buildingData.city && buildingData.state && buildingData.zip_code 
        ? `, ${buildingData.city}, ${buildingData.state} ${buildingData.zip_code}`
        : buildingData.city 
        ? `, ${buildingData.city}`
        : '';
      return `${buildingData.address}${aptPart}${cityStateZip}`;
    }
    return '';
  };

  // Get prepared for information
  const getPreparedForName = () => {
    if (data?.prepared_for_name) return data.prepared_for_name;
    if (buildingData?.client_name) return buildingData.client_name;
    return 'Lee Odell';
  };

  const getPreparedForCompany = () => {
    if (data?.client_name) return data.client_name;
    return '';
  };

  const getPreparedForAddress = () => {
    if (data?.client_city && data?.client_state) {
      return `${data.client_address || ''} ${data.client_city}, ${data.client_state} ${data.client_zip_code || ''}`.trim();
    }
    return '';
  };

  // Format dates
  const reportDate = data?.inspection_date 
    ? moment(data.inspection_date).format('MM/DD/YYYY')
    : moment().format('MM/DD/YYYY');
  
  const inspectionDate = data?.inspection_date 
    ? moment(data.inspection_date).format('MM/DD/YYYY')
    : moment().format('MM/DD/YYYY');

  // Building image URL - convert to data URL to avoid CORS
  const buildingImageUrl = data?.building_image 
    ? `${baseUrl}${data.building_image}`
    : '';
  
  let buildingImageDataUrl = '';
  if (buildingImageUrl) {
    try {
      buildingImageDataUrl = await convertImageToDataURL(buildingImageUrl);
      if (!buildingImageDataUrl) {
        console.warn('Building image conversion failed, using URL directly');
        buildingImageDataUrl = buildingImageUrl;
      } else {
        console.log('Building image converted to data URL successfully');
      }
    } catch (error) {
      console.warn('Failed to convert building image to data URL:', error);
      buildingImageDataUrl = buildingImageUrl;
    }
  }

  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 12px;
          color: #000;
          background: #fff;
          padding: 18px 24px 10px 24px;
        }
        .document-container{
        padding: 54px 72px;
        }
        ${getHeaderStyles()}
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 30px 0;
          padding: 10px 0;
          color: #000;
        }
        .content-container {
          display: flex;
          gap: 25px;
          margin-top: 25px;
          padding-top: 10px;
        }
        .left-column {
          flex: 1;
          max-width: 50%;
        }
        .right-column {
          flex: 1;
          max-width: 50%;
        }
        .report-field {
          margin-bottom: 15px;
          line-height: 1.6;
          padding: 3px 0;
        }
        .report-label {
          font-weight: bold;
          margin-bottom: 2px;
          color: #000;
        }
        .report-value {
          color: #333;
          margin-left: 0;
        }
        .report-value div {
          margin-bottom: 2px;
        }
        .building-image-container {
          width: 100%;
          height: auto;
          margin-top: 15px;
          padding: 5px;
        }
        .building-image {
          width: 100%;
          height: auto;
          max-height: 200mm;
          object-fit: cover;
          border: 1px solid #ddd;
        }
        .image-placeholder {
          width: 100%;
          height: 200px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
      ${moldType === 'pre' ? 'MOLD ASSESSMENT AND REMEDIATION WORK PLAN' : 'MOLD POST ASSESSMENT'}
      </div>

      <div class="content-container">
        <div class="left-column">
          <div class="report-field">
            <div class="report-label">Prepared by:</div>
            <div class="report-value">
              <div>${getInspectorName()}</div>
              <div>${ticketData?.company_name || 'Raza Environmental Inc.'}</div>
            </div>
          </div>

          <div class="report-field">
            <div class="report-label">NYS DOL License ID #:</div>
            <div class="report-value">
              <div>Assessor License: ${inspectorCertificate?.certificate_id}</div>
              <div>${ticketData?.company_license ? 'Business License ' + ticketData?.company_license : ''}</div>
            </div>
          </div>

          <div class="report-field">
            <div class="report-label">Report Prepared On:</div>
            <div class="report-value">${reportDate}</div>
          </div>

          <div class="report-field">
            <div class="report-label">Property Address:</div>
            <div class="report-value">${getPropertyAddress()}</div>
          </div>

          <div class="report-field">
            <div class="report-label">Prepared for:</div>
            <div class="report-value">
              <div>${getPreparedForName()}</div>
              ${getPreparedForCompany() ? `<div>${getPreparedForCompany()}</div>` : ''}
              ${getPreparedForAddress() ? `<div>${getPreparedForAddress()}</div>` : ''}
            </div>
          </div>

          <div class="report-field">
            <div class="report-label">HPD Violation:</div>
            <div class="report-value">#${data?.violation_id || ''}</div>
          </div>

          <div class="report-field">
            <div class="report-label">Date of Inspection:</div>
            <div class="report-value">${inspectionDate}</div>
          </div>
        </div>

        <div class="right-column">
          <div class="building-image-container">
            ${buildingImageDataUrl 
              ? `<img src="${buildingImageDataUrl}" alt="Property Building" class="building-image" />`
              : buildingImageUrl
              ? `<img src="${buildingImageUrl}" alt="Property Building" class="building-image" crossorigin="anonymous" />`
              : '<div class="image-placeholder">No building image available</div>'
            }
          </div>
        </div>
      </div>
      </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for all images to load
    await waitForImages(tempDiv);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: 1123, // A4 height in pixels at 96 DPI (297mm)
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297); // A4 size in mm
  } catch (error) {
    console.error('Error generating HTML to PDF:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the Remediation Work Plan page
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createRemediationWorkPlanPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null) => {
  const rooms = data?.rooms || [];
  
  // Generate remediation text for each room
  const generateRemediationText = (room) => {
    const actions = [];
    
    // Build comprehensive remediation text based on room conditions
    let remediationText = '';
    
    // Check for visible mold or moisture issues
    const hasMold = room.visible_mold || room.visible_moisture || room.visible_surface_damage;
    
    if (hasMold) {
      // Start with cleaning and treatment
      remediationText = 'Clean and treat all affected surfaces with EPA approved antimicrobial solution to remove surface mold growth. ';
      
      // Add specific remediation actions
      if (room.surface_cleaned_and_treated) {
        remediationText += 'Apply EPA approved shockwave to destroy surface mold and remove embedded mold roots. ';
      }
      
      if (room.drywall_replaced) {
        remediationText += 'Remove and replace damaged drywall that cannot be effectively cleaned and treated. ';
      }
      
      if (room.cabinet_and_wall_replaced) {
        remediationText += 'Remove and replace damaged cabinets and wall materials that are beyond repair. ';
      }
      
      if (room.caulk_repaired) {
        remediationText += 'Repair and reseal caulking around fixtures to prevent water intrusion. ';
      }
      
      // Always include ventilation recommendation
      if (room.ventilation_created) {
        remediationText += 'Create a source of ventilation, one source of ventilation would be to install a fan within the area to help control moisture build up to prevent future growth of mold. Refrain from using excessive moisture and ensure proper ventilation.';
      } else {
        remediationText += 'Create a source of ventilation to help control moisture build up to prevent future growth of mold. Ensure proper ventilation is maintained.';
      }
    } else {
      // Default remediation for rooms without specific issues
      remediationText = 'Clean and treat all affected surfaces. Remove damaged materials that cannot be effectively cleaned. Create a source of ventilation to help control moisture build up to prevent future growth of mold. Ensure proper ventilation is maintained.';
    }
    
    return remediationText.trim();
  };

  // Build remediation table rows
  const remediationRows = rooms.map((room, index) => {
    const roomName = room.room_name || room.name || `Room #${index + 1}`;
    const remediation = generateRemediationText(room);
    return { area: roomName, remediation };
  });

  // If no rooms, add a default row
  if (remediationRows.length === 0) {
    remediationRows.push({
      area: 'Affected Areas',
      remediation: 'Clean and treat all affected surfaces. Remove damaged materials that cannot be effectively cleaned. Ensure proper ventilation to prevent future mold growth.'
    });
  }

  // Format estimated cost
  const estimatedCost = data?.estimated_cost 

  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
          .document-container{
        padding: 54px 72px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 10px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 20px 0;
          padding: 10px 0;
          color: #000;
        }
        .remediation-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 10px;
        }
        .remediation-table th {
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
          font-weight: bold;
        }
        .remediation-table td {
          border: 1px solid #ddd;
          padding: 8px;
          vertical-align: top;
        }
        .remediation-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .section-title {
          font-size: 13px;
          font-weight: bold;
          margin: 20px 0 10px 0;
          color: #000;
        }
        .section-content {
          font-size: 10px;
          line-height: 1.6;
          margin-bottom: 15px;
        }
        .section-content p {
          margin-bottom: 8px;
        }
        .estimate-text {
          font-weight: bold;
          margin: 10px 0;
        }
        .exclusions-list {
          margin: 10px 0 10px 20px;
        }
        .exclusions-list li {
          margin-bottom: 5px;
        }
        .process-list {
          margin: 10px 0 10px 20px;
        }
        .process-list li {
          margin-bottom: 8px;
          line-height: 1.5;
        }
        .process-title {
          font-weight: bold;
          margin-top: 8px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
        REMEDIATION WORK PLAN
      </div>

      <table class="remediation-table">
        <thead>
          <tr>
            <th style="width: 25%;">Area</th>
            <th style="width: 75%;">Remediation</th>
          </tr>
        </thead>
        <tbody>
          ${remediationRows.map(row => `
            <tr>
              <td>${row.area}</td>
              <td>${row.remediation}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="section-title">Mold Remediation Estimate:</div>
      <div class="section-content">
        <p>Please obtain quotes from a few EPA approved contractors to confirm pricing:</p>
        <p class="estimate-text">Abatement ${estimatedCost} + tax approximate.</p>
        <p style="margin-top: 10px;">The following items are not included:</p>
        <ul class="exclusions-list">
          <li>Repairs, leaks and services subcontractor costs are not included.</li>
          <li>Tile & grout repair work not included.</li>
          <li>Mechanical ventilation not included.</li>
          <li>Hazardous materials removal not included.</li>
          <li>Assessor fees & filing not included.</li>
        </ul>
        <p style="margin-top: 10px;"><strong>These are estimates ONLY.</strong></p>
      </div>

      <div class="section-title">BREAKDOWN PROCESS FOR MOLD REMEDIATION:</div>
      <div class="section-content">
        <ul class="process-list">
          <li>
            <span class="process-title">Containment Barriers:</span> 
            <p>Place barriers to prevent cross-contamination, sealing ducts/grills, and isolating the work area.</p>
          </li>
          <li>
            <span class="process-title">Cover and protect:</span> 
            <p>Cover or remove furniture and discard non-cleanable materials.</p>
          </li>
          <li>
            <span class="process-title">Air filtration/also known as air scrubbers/Negative Air Pressure:</span>
            <p>Use HEPA-filtered devices to maintain air quality and create a vacuum effect.</p>
          </li>
          <li>
            <span class="process-title">Supress dust:</span> 
            <p>Spray moldy surfaces with water/soap before cleaning.</p>
          </li>
          <li>
            <span class="process-title">Apply antimicrobial:</span> 
            <p>Apply EPA approved shockwave to destroy surface mold.</p>
          </li>
          <li>
            <span class="process-title">Embedded mold removal:</span>
            <p>Apply EPA approved shockwave to remove embedded mold roots.</p>
          </li>
          <li>
            <span class="process-title">Scrub and wipe clean:</span> 
            <p>Scrub and wipe off all surface mold from building material.</p>
          </li>
       

       
        <li>
          <span class="process-title">HEPA vacuum:</span>
          <p>After cleaning, HEPA vacuum all surfaces to remove mold spores, roots, and dust. This step is crucial to ensure all mold particles are removed from the affected area and prevent recontamination.</p>
        </li>
        <li>
          <span class="process-title">Mold-proof coating / encapsulate:</span>
          <p>Apply a mold-resistant coating to prevent reoccurrence. We recommend using "Forster 40-20 Fungicidal protective coating" or an equivalent EPA approved product. This coating will help prevent future mold growth by creating a protective barrier on treated surfaces.</p>
        </li>
        <li>
          <span class="process-title">Garbage removal:</span>
          <p>Properly dispose of all contaminated cleaning waste, including plastic sheeting, cleaning materials, and tools used during remediation. All waste must be sealed in heavy-duty bags and disposed of according to local regulations. Ensure all contaminated materials are removed from the property to prevent cross-contamination.</p>
        </li>
        <li>
          <span class="process-title">Post remediation removal:</span>
          <p>A post-remediation inspection will be performed to verify cleanup success and property safety. This inspection ensures that all mold has been properly removed and the property is safe for occupancy.</p>
          <p style="margin-top: 10px;">This work is performed in compliance with Article 32 of NYS Labor Law (NYS DOL) and New York City Administrative Code §24-154 for mold condition correction, including assessments, remediation work, and report sharing. All remediation activities must be documented and reported in accordance with these regulations.</p>
        </li>
      </ul>
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    const contentHeight = Math.ceil(tempDiv.scrollHeight);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: contentHeight,
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    addCanvasToPDF(doc, canvas);
  } catch (error) {
    console.error('Error generating remediation work plan page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the breakdown process page for mold remediation (for 'post' type)
 * @param {jsPDF} doc - The PDF document
 * @param {string} logoDataUrl - Data URL for the logo image
 */
const createBreakdownProcessPage = async (doc, logoDataUrl = '', ticketData = null) => {
  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 54px 72px;
        }
        ${getHeaderStyles()}
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 30px 0;
          padding: 10px 0;
          color: #000;
        }
        .section-title {
          font-size: 14px;
          font-weight: bold;
          margin: 25px 0 15px 0;
          color: #000;
        }
        .process-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .process-list li {
          margin-bottom: 20px;
        }
        .process-title {
          font-weight: bold;
          text-decoration: underline;
          display: block;
          margin-bottom: 5px;
          font-size: 12px;
        }
        .process-description {
          font-size: 11px;
          line-height: 1.6;
          color: #333;
          margin-top: 5px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
        BREAKDOWN PROCESS FOR MOLD REMEDIATION:
      </div>

      <div class="content-section">
        <ul class="process-list">
          <li>
            <span class="process-title">Containment Barriers</span>
            <div class="process-description">
              Containment barriers are put in place to avoid cross-contamination of the unaffected areas of your property. Seal ventilation ducts/grills and other openings in the work area with plastic sheeting; isolate the work area with plastic sheeting and cover egress pathways.
            </div>
          </li>
          <li>
            <span class="process-title">Cover and protect</span>
            <div class="process-description">
              Remove or securely cover with plastic sheeting any furniture or other items in the work area that cannot be moved. Remove and discard any materials that cannot be properly cleaned.
            </div>
          </li>
          <li>
            <span class="process-title">Air filtration/also known as air scrubbers/Negative Air Pressure</span>
            <div class="process-description">
              Hepa-filtered air filtration devices are used to keep the air quality in your property safe and clean during your remediation. For Negative Air Pressure filtered air is exhausted outside of the containment area. This creates a vacuum effect, which helps limit the spread of contaminants to other areas inside the structure.
            </div>
          </li>
          <li>
            <span class="process-title">Supress dust</span>
            <div class="process-description">
              Gently spray the moldy surface with a solution of water and soap or detergent before cleaning to limit the spread of dust.
            </div>
          </li>
          <li>
            <span class="process-title">Apply antimicrobial</span>
            <div class="process-description">
              EPA approved shockwave is applied to affected areas to destroy surface mold and aid in the surface cleaning process.
            </div>
          </li>
          <li>
            <span class="process-title">Embedded mold removal</span>
            <div class="process-description">
              EPA approved shockwave is applied to remove embedded mold roots and minimize mold staining.
            </div>
          </li>
          <li>
            <span class="process-title">Scrub and wipe clean</span>
            <div class="process-description">
              All surface mold is scrubbed, wiped and cleaned off of affected or contaminated building material.
            </div>
          </li>
          <li>
            <span class="process-title">HEPA vacuum</span>
            <div class="process-description">
              HEPA vacuuming is done after cleaning to completely capture and remove any remaining mold spores and roots and/or dust.
            </div>
          </li>
          <li>
            <span class="process-title">Mold-proof coating / encapsulate</span>
            <div class="process-description">
              A mold resistant coating is applied to cleaned surfaces to help prevent mold from reoccurring, recommended is Forster 40-20 Fungicidal protective coating.
            </div>
          </li>
          <li>
            <span class="process-title">Garbage removal</span>
            <div class="process-description">
              Properly remove and discard from property all contaminated cleaning-related waste, including plastic sheeting, cleaning tools and other materials, in heavy-duty plastic garbage bags and seal securely.
            </div>
          </li>
          <li>
            <span class="process-title">Post remediation removal</span>
            <div class="process-description">
              Post remediation inspection will be performed to verify that cleanup was successful, and your property is safe. * Comply with Article 32 of NYS Labor Law (NYS DOL) and New York City Administrative Code §24-154 when correcting a mold condition, this includes conducting mold assessments, remediation work and the sharing of notices and reports.
            </div>
          </li>
        </ul>
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for all images to load
    await waitForImages(tempDiv);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794,
      height: 1123,
      onclone: async (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve();
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297);
  } catch (error) {
    console.error('Error generating breakdown process page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the third page with additional remediation steps
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createRemediationStepsPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null) => {
  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 54px 72px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 10px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .content-section {
          margin: 25px 0;
        }
        .section-heading {
          font-size: 13px;
          font-weight: bold;
          margin-bottom: 8px;
          color: #000;
        }
        .section-text {
          font-size: 11px;
          line-height: 1.7;
          color: #333;
          margin-bottom: 20px;
        }
        .section-text p {
          margin-bottom: 5px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="content-section">
        <div class="section-heading">HEPA vacuum</div>
        <div class="section-text">
          <p>After cleaning, HEPA vacuum all surfaces to remove mold spores, roots, and dust. This step is crucial to ensure all mold particles are removed from the affected area and prevent recontamination.</p>
        </div>

        <div class="section-heading">Mold-proof coating / encapsulate</div>
        <div class="section-text">
          <p>Apply a mold-resistant coating to prevent reoccurrence. We recommend using "Forster 40-20 Fungicidal protective coating" or an equivalent EPA approved product. This coating will help prevent future mold growth by creating a protective barrier on treated surfaces.</p>
        </div>

        <div class="section-heading">Garbage removal</div>
        <div class="section-text">
          <p>Properly dispose of all contaminated cleaning waste, including plastic sheeting, cleaning materials, and tools used during remediation. All waste must be sealed in heavy-duty bags and disposed of according to local regulations. Ensure all contaminated materials are removed from the property to prevent cross-contamination.</p>
        </div>

        <div class="section-heading">Post remediation removal</div>
        <div class="section-text">
          <p>A post-remediation inspection will be performed to verify cleanup success and property safety. This inspection ensures that all mold has been properly removed and the property is safe for occupancy.</p>
          <p style="margin-top: 10px;">This work is performed in compliance with Article 32 of NYS Labor Law (NYS DOL) and New York City Administrative Code §24-154 for mold condition correction, including assessments, remediation work, and report sharing. All remediation activities must be documented and reported in accordance with these regulations.</p>
        </div>
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: 1123, // A4 height in pixels at 96 DPI (297mm)
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297); // A4 size in mm
  } catch (error) {
    console.error('Error generating remediation steps page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the fourth page with Assessment Details
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createAssessmentDetailsPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null, moldType = 'pre') => {
  const rooms = data?.rooms || [];
  
  // Get property address
  const getPropertyAddress = () => {
    if (data?.property_address) return data.property_address;
    if (buildingData?.address) {
      const aptPart = apartmentNumber ? ` ${apartmentNumber}` : '';
      const cityStateZip = buildingData.city && buildingData.state && buildingData.zip_code 
        ? ` ${buildingData.city}, ${buildingData.state} ${buildingData.zip_code}`
        : buildingData.city 
        ? ` ${buildingData.city}`
        : '';
      return `${buildingData.address}${aptPart}${cityStateZip}`;
    }
    return '';
  };

  // Format assessment date
  const assessmentDate = data?.inspection_date 
    ? moment(data.inspection_date).format('MM/DD/YYYY')
    : moment().format('MM/DD/YYYY');

  // Get assessor info
  const assessorName = data?.prepared_by_name || ticketData?.inspector_name || 'Zamo Kucevic';
  const assessorCompany = data?.prepared_by_company || 'Raza Environmental Inc.';
  const businessLicense = data?.business_license || 'Business License #01695';
  const assessorInfo = `${assessorName}, Assessor at ${assessorCompany} NYS DOL ${businessLicense.replace('Business License #', '#')}`;

  // Get property identifiers
  const block = buildingData?.block || data?.block || '';
  const lot = buildingData?.lot || data?.lot || '';
  const bin = buildingData?.bin || data?.bin || '';
  const propertyIdentifiers = [];
  if (block) propertyIdentifiers.push(`Block (${block})`);
  if (lot) propertyIdentifiers.push(`Lot (${lot})`);
  if (bin) propertyIdentifiers.push(`Bin (${bin})`);

  const conclusionText = data?.final_conclusion || 
    'The results of this assessment have confirmed the presence of damaged materials and/or mold growth as estimated; recommendations provided are based on the visible conditions and are intended to support the required scope of work.';

  // Get outdoor weather
  const outdoorTemp = data?.outside_temperature ? `${parseFloat(data.outside_temperature).toFixed(1)}F` : 'N/A';
  const outdoorHumidity = data?.humidity ? `${parseFloat(data.humidity).toFixed(1)}%` : 'N/A';

  // Generate visual findings for each room
  const generateVisualFindings = (room) => {
    const findings = [];
    
    if (room.visible_surface_damage === true) {
      findings.push('Yes visible surface damage/discoloration was observed at the time of inspection.');
    } else if (room.visible_surface_damage === false || !room.visible_surface_damage) {
      findings.push('No visible surface damage/discoloration was observed at the time of inspection.');
    }
    
    if (room.visible_mold === true) {
      findings.push('Yes visible mold was detected at the time of inspection');
    } else if (room.visible_mold === false || !room.visible_mold) {
      findings.push('No visible mold was detected at the time of inspection');
    }
    
    if (room.visible_moisture === false || !room.visible_moisture) {
      findings.push('No visible moisture was detected at the time of inspection');
    } else if (room.visible_moisture === true) {
      findings.push('Yes visible moisture was detected at the time of inspection');
    }
    
    if (room.musty_odor === true) {
      findings.push('Yes Odor was detected at the time of inspection.');
    } else if (room.musty_odor === false || !room.musty_odor) {
      findings.push('No Odor was detected at the time of inspection.');
    }
    
   
    
    return findings;
  };

  // Build observation table rows
  const observationRows = rooms.map((room, index) => {
    const roomName = room.room_name || room.name || `Room #${index + 1}`;
    const visualFindings = generateVisualFindings(room);
    const squareFeet = room.square_feet_affected 
      ? `${parseFloat(room.square_feet_affected).toFixed(0)}ft`
      : '0ft';
    
    return {
      area: roomName,
      findings: visualFindings,
      squareFeet: squareFeet
    };
  });



  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 30px 50px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
          padding-bottom: 5px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 10px 0;
          padding: 5px 0;
          color: #000;
        }
        .details-section {
          margin: 10px 0;
        }
        .detail-item {
          font-size: 11px;
          line-height: 1.3;
          margin-bottom: 3px;
        }
        .detail-label {
          font-weight: bold;
          margin-right: 5px;
        }
        .weather-section {
          margin: 10px 0;
        }
        .weather-title {
          font-size: 12px;
          font-weight: bold;
          margin-bottom: 5px;
          text-decoration: underline;
        }
        .weather-item {
          font-size: 11px;
          margin-bottom: 3px;
        }
        .interior-weather {
          margin-top: 8px;
        }
        .room-weather {
          margin-left: 15px;
          margin-bottom: 5px;
        }
        .room-name {
          font-weight: bold;
          margin-bottom: 2px;
        }
        .section-title {
          font-size: 13px;
          font-weight: bold;
          margin: 12px 0 5px 0;
          text-decoration: underline;
          color: #000;
        }
        .section-subtitle {
          font-size: 12px;
          font-weight: bold;
          margin: 8px 0 4px 0;
          color: #000;
        }
        .section-text {
          font-size: 11px;
          line-height: 1.4;
          margin-bottom: 8px;
        }
        .section-text p {
          margin-bottom: 8px;
        }
        .observation-table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
          font-size: 10px;
        }
        .observation-table th {
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          padding: 5px;
          text-align: left;
          font-weight: bold;
        }
        .observation-table td {
          border: 1px solid #ddd;
          padding: 5px;
          vertical-align: top;
        }
        .observation-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .findings-list {
          margin: 0;
          padding-left: 0;
          list-style: none;
        }
        .findings-list li {
          margin-bottom: 2px;
          line-height: 1.3;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
        ASSESSMENT DETAILS
      </div>

      <div class="details-section">
        <div class="detail-item">
        
          <span>${assessorInfo}</span> conducted an assessment on <span>${assessmentDate}</span> , <span>${ticketData?.company_address}</span>
        </div>
      </div>

      ${moldType === 'pre' ? `<div class="weather-section">
        <div class="weather-title">OUTDOOR WEATHER CONDITIONS:</div>
        <div class="weather-item">
          <span class="detail-label">Temperature:</span>
          <span>${outdoorTemp}</span>
        </div>
        <div class="weather-item">
          <span class="detail-label">Humidity%:</span>
          <span>${outdoorHumidity}</span>
        </div>

        <div class="interior-weather">
          <div class="weather-title">INTERIOR WEATHER CONDITIONS:</div>
          ${rooms.map((room, index) => {
            const roomName = room.room_name || room.name || `Room #${index + 1}`;
            const roomTemp = room.temperature ? `${parseFloat(room.temperature).toFixed(1)}F` : 'N/A';
            const roomHumidity = room.humidity ? `${parseFloat(room.humidity).toFixed(1)}%` : 'N/A';
            return `
              <div class="room-weather">
                <div class="room-name">${roomName}:</div>
                <div class="weather-item">
                  <span class="detail-label">Temperature:</span>
                  <span>${roomTemp}</span>
                </div>
                <div class="weather-item">
                  <span class="detail-label">RH%:</span>
                  <span>${roomHumidity}</span>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>  ` : ''}

      <div class="section-title">BACKGROUND DETAILS</div>
      <div class="section-subtitle">Background Details</div>
      ${moldType === 'pre' ? `<div class="section-text">
        <p>This assessment was performed in response to HPD Violation #${data?.violation_id || 'N/A'} and is limited strictly to a non-invasive visual inspection of the areas accessible at the time of the site visit.</p>
        
        <p style="margin-top: 8px; font-weight: bold;">The purpose of this inspection is to:</p>
        <ul style="margin-top: 4px; margin-left: 20px; padding-left: 20px;">
          <li style="margin-bottom: 3px;">Document current visible mold conditions</li>
          <li style="margin-bottom: 3px;">Develop an appropriate mold remediation plan in accordance with NYC guidelines</li>
          <li style="margin-bottom: 3px;">Provide recommendations for corrective actions required to clear the violation</li>
        </ul>
        
        <p style="margin-top: 8px; font-weight: bold;">This inspection is visual-only and non-destructive. Accordingly:</p>
        <ul style="margin-top: 4px; margin-left: 20px; padding-left: 20px;">
          <li style="margin-bottom: 3px;">No wall, ceiling, or floor assemblies were opened or intruded into</li>
          <li style="margin-bottom: 3px;">No assessment was performed on concealed spaces or conditions behind walls, inside cavities, or within mechanical systems</li>
          <li style="margin-bottom: 3px;">No moisture mapping, air sampling, swab sampling, or laboratory testing was conducted unless otherwise stated</li>
          <li style="margin-bottom: 3px;">Observations are based solely on conditions visible and accessible at the time of inspection</li>
        </ul>
        
        <p style="margin-top: 8px; font-weight: bold;">Because this assessment does not include destructive or invasive investigation:</p>
        <ul style="margin-top: 4px; margin-left: 20px; padding-left: 20px;">
          <li style="margin-bottom: 3px;">The inspector cannot confirm the presence, cause, or extent of mold growth or moisture conditions within concealed building components</li>
          <li style="margin-bottom: 3px;">The inspector bears no responsibility for conditions hidden behind walls or other inaccessible areas</li>
          <li style="margin-bottom: 3px;">Additional concealed mold, moisture intrusion, or structural deterioration may exist but is outside the scope of this inspection</li>
        </ul>
        
        <p style="margin-top: 8px;">This report reflects only the observable mold conditions relevant to HPD Violation #${data?.violation_id || 'N/A'} and provides a remediation plan based on those accessible findings.</p>
      </div>` : `<div class="section-text">
        <p>An initial mold assessment was performed, and a Mold Remediation Work Plan was created on ${data?.work_plan_date ? moment(data.work_plan_date).format('MMMM DD, YYYY') : moment().format('MMMM DD, YYYY')}, in response to conditions associated with HPD Violation #${data?.violation_id || 'N/A'}.</p>
        
        <p style="margin-top: 8px;">The remediation work described in that plan has since been completed by the responsible contractor.</p>
        
        <p style="margin-top: 8px;">This post-clearance inspection is being performed to verify that the remediation work was completed in accordance with the approved remediation plan and applicable NYC regulations.</p>
        
        <p style="margin-top: 8px;">This inspection is visual-only and non-invasive, intended solely to confirm visible cleanliness, dryness, and correction of previously identified mold conditions.</p>
      </div>`}

      <div class="section-subtitle">Observation</div>

      <table class="observation-table">
        <thead>
          <tr>
            <th style="width: 20%;">Area</th>
            <th style="width: 60%;">Visual Findings</th>
           ${moldType === 'pre' ? `<th style="width: 20%;">Square Feet Affected</th>` : ''}
          </tr>
        </thead>
        <tbody>
          ${observationRows.map(row => `
            <tr>
              <td>${row.area}</td>
              <td>
                <ul class="findings-list">
                  ${row.findings.map(finding => `<li>${finding}</li>`).join('')}
                </ul>
              </td>
              ${moldType === 'pre' ? `<td>${row.squareFeet}</td>` : ''}
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="section-title">CONCLUSION</div>
      <div class="section-subtitle">Conclusion</div>
      <div class="section-text">
        <p>${moldType === 'pre' ? conclusionText.replace(/\n/g, '<br />') : data?.clearance_passed ? 'Mold Assessment Passed' : 'Mold Assessment Failed'}</p>
      </div>
     
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    const contentHeight = Math.ceil(tempDiv.scrollHeight);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: contentHeight,
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    addCanvasToPDF(doc, canvas);
  } catch (error) {
    console.error('Error generating assessment details page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the fifth page with Conclusion
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createConclusionPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null, moldType = 'pre') => {
  // Get conclusion text from data or use default
  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 54px 72px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 10px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .content-section {
          margin: 40px 0;
        }
        .section-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 30px 0 20px 0;
          color: #000;
        }
        .section-subtitle {
          font-size: 13px;
          font-weight: bold;
          margin: 20px 0 15px 0;
          color: #000;
        }
        .section-text {
          font-size: 11px;
          line-height: 1.8;
          color: #333;
          margin-bottom: 30px;
          text-align: justify;
        }
        .disclaimer-title {
          font-size: 13px;
          font-weight: bold;
          margin: 30px 0 15px 0;
          color: #000;
        }
        .disclaimer-text {
          font-size: 11px;
          line-height: 1.8;
          color: #333;
          text-align: justify;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="content-section">
        <div class="section-title">CONCLUSION</div>
        <div class="section-subtitle">Conclusion</div>
        <div class="section-text">
           <p>${moldType === 'pre' ? (data?.final_conclusion || '').replace(/\n/g, '<br />') : data?.clearance_passed ? 'Mold Assessment Passed' : 'Mold Assessment Failed'}</p>
        </div>

       
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: 1123, // A4 height in pixels at 96 DPI (297mm)
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297); // A4 size in mm
  } catch (error) {
    console.error('Error generating conclusion page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the sixth page with Limited Evaluation terms
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createLimitedEvaluationPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null) => {
  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 54px 72px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 10px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 25px 0 30px 0;
          padding: 10px 0;
          color: #000;
        }
        .content-section {
          margin: 20px 0;
        }
        .section-heading {
          font-size: 13px;
          font-weight: bold;
          margin: 25px 0 12px 0;
          color: #000;
        }
        .section-text {
          font-size: 11px;
          line-height: 1.8;
          color: #333;
          margin-bottom: 20px;
          text-align: justify;
        }
        .section-text p {
          margin-bottom: 10px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
        MOLD INSPECTION AND ASSESSMENT LIMITED EVALUATION
      </div>

      <div class="content-section">

        <div class="section-heading">Introduction</div>
        <div class="section-text">
          <p>This assessment is non-intrusive, visual and thermograph examination performed for a fee, and is limited to
work zones. This inspection includes ONLY visual and thermography readily accessible components and
systems. Mold sampling and laboratory testing are optional and only performed with the client's acceptance
of additional fees. It is important to understand that this mold inspection is NOT a home inspection, as a
result, physical defects in the systems will not be identified.</p>
        </div>
       
 <div class="section-heading"></div>
        <div class="section-text">
          <p>This assessment will be performed in accordance with the Industry Standards, systems and components to
be inspected for signs of potential mold growth and conditions that are conducive to mold growth, No
destructive testing or invasive sampling were performed for the purpose of this assessment. The conditions
cited in this report refer only to accessible or observable materials and interior finishes. Room/area
designation are based on current use of room when floor plans are not available. The estimated square feet
(sq.ft) of affected mold/damaged materials are based on visual observations and or moisture meter testing.
Please note that the identified boundaries of water damaged materials/affected mold surfaces are subject
to change, increase or decrease, based on the conditions of the subject property. The elapsed time period
between this assessment and when the remedial actions are taken can change the boundaries of the
identified water damaged/affected mold surfaces/areas. This elapsed time can also result in potential mold
growth which was not present during the initial assessment. All field measured dimensions and square
footage should be considered approximations.</p>
        </div>

         <div class="section-heading">DISCLAIMER:</div>
        <div class="section-text">
          <p>The Assessor believes the information contained within this assessment report to be correct at the time of
printing. The assessor does not accept responsibility for any consequences arising from the use of the
information herein. The report is based on matters which were observed or came to the attention of the
assessor during the day of the assessment and should not be relied upon as an exhaustive record of all
possible risks or hazards that may exist or potential improvement that can be made. If any illness is
experienced that may be related to mold or other indoor environmental factors, clients should contact
professional medical advice. No destructive or disruptive testing or assessment will be performed.</p>
        </div>

        <div class="section-heading">Limitations:</div>
        <div class="section-text">
          <p>Evaluation and test results do not guarantee an indoor environment free of contaminants. The accuracy, precision, and uncertainty of instruments and methods used, as well as environmental and climatic conditions, may affect the results obtained.</p>
          <p>Destructive testing was not performed during this evaluation. The assessment only covers visible conditions at the time of inspection and does not include areas that were inaccessible, concealed, or not readily observable.</p>
          <p>Raza Environmental Inc.'s opinions are based on professional experience and industry standards. No implied warranty or guarantee is provided regarding the accuracy, completeness, or reliability of the information contained in this report.</p>
        </div>

        <div class="section-heading">Disputes:</div>
        <div class="section-text">
          <p>The Inspector/Inspection company is not an insurer. The price of this report is solely for the service provided and does not constitute insurance coverage for any defects, conditions, or issues that may be discovered or arise in the future.</p>
          <p>Clients must report any claim of inaccuracy within 5 business days of receiving the report, or the claim is waived. The company has five business days to respond to any such claim. If the claim is not satisfied after the company's response, liability is limited to a refund of the inspection and report price.</p>
        </div>
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: 1123, // A4 height in pixels at 96 DPI (297mm)
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297); // A4 size in mm
  } catch (error) {
    console.error('Error generating limited evaluation page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates the seventh page with measurement images
 * @param {jsPDF} doc - The PDF document
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createMeasurementImagesPage = async (doc, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';

  // Get image URLs and convert to data URLs to avoid CORS
  const temperatureImageUrl = data?.temperature_image 
    ? `${baseUrl}${data.temperature_image}`
    : '';
  
  const humidityImageUrl = data?.humidity_image 
    ? `${baseUrl}${data.humidity_image}`
    : '';
  
  const apartmentImageUrl = data?.unit_image 
    ? `${baseUrl}${data.unit_image}`
    : '';

  // Convert all images to data URLs
  let temperatureImageDataUrl = '';
  let humidityImageDataUrl = '';
  let apartmentImageDataUrl = '';

  if (temperatureImageUrl) {
    try {
      temperatureImageDataUrl = await convertImageToDataURL(temperatureImageUrl);
      if (!temperatureImageDataUrl) {
        console.warn('Temperature image conversion failed, using URL directly');
        temperatureImageDataUrl = temperatureImageUrl;
      } else {
        console.log('Temperature image converted to data URL successfully');
      }
    } catch (error) {
      console.warn('Failed to convert temperature image to data URL:', error);
      temperatureImageDataUrl = temperatureImageUrl;
    }
  }

  if (humidityImageUrl) {
    try {
      humidityImageDataUrl = await convertImageToDataURL(humidityImageUrl);
      if (!humidityImageDataUrl) {
        console.warn('Humidity image conversion failed, using URL directly');
        humidityImageDataUrl = humidityImageUrl;
      } else {
        console.log('Humidity image converted to data URL successfully');
      }
    } catch (error) {
      console.warn('Failed to convert humidity image to data URL:', error);
      humidityImageDataUrl = humidityImageUrl;
    }
  }

  if (apartmentImageUrl) {
    try {
      apartmentImageDataUrl = await convertImageToDataURL(apartmentImageUrl);
      if (!apartmentImageDataUrl) {
        console.warn('Apartment image conversion failed, using URL directly');
        apartmentImageDataUrl = apartmentImageUrl;
      } else {
        console.log('Apartment image converted to data URL successfully');
      }
    } catch (error) {
      console.warn('Failed to convert apartment image to data URL:', error);
      apartmentImageDataUrl = apartmentImageUrl;
    }
  }

  // Get dynamic values
  const outdoorTemperature = data?.outside_temperature 
    ? `${parseFloat(data.outside_temperature).toFixed(1)}°F`
    : 'N/A';
  
  const outdoorHumidity = data?.humidity 
    ? `${parseFloat(data.humidity).toFixed(1)}%`
    : 'N/A';
  
  const apartmentNum = data?.apartment_number || apartmentNumber || 'N/A';

  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 54px 72px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 10px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .measurements-container {
          display: flex;
          gap: 20px;
          margin: 30px 0;
          justify-content: center;
        }
        .measurement-item {
          flex: 1;
          max-width: 45%;
          text-align: center;
        }
        .measurement-image {
          width: 70%;
          max-width: 210px;
          height: auto;
          border: 1px solid #ddd;
          margin-bottom: 10px;
          object-fit: contain;
        }
        .image-placeholder {
          width: 70%;
          max-width: 210px;
          height: 200px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
          margin: 0 auto 10px auto;
        }
        .measurement-label {
          font-size: 12px;
          font-weight: bold;
          color: #000;
          margin-top: 10px;
        }
        .apartment-section {
          margin: 40px 0;
          text-align: center;
        }
        .apartment-image {
          width: 70%;
          max-width: 210px;
          height: auto;
          border: 1px solid #ddd;
          margin: 0 auto 10px auto;
          display: block;
          object-fit: contain;
        }
        .apartment-placeholder {
          width: 70%;
          max-width: 210px;
          height: 300px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
          margin: 0 auto 10px auto;
        }
        .apartment-label {
          font-size: 12px;
          font-weight: bold;
          color: #000;
          margin-top: 10px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="measurements-container">
        <div class="measurement-item">
          ${temperatureImageDataUrl 
            ? `<img src="${temperatureImageDataUrl}" alt="Outdoor Temperature" class="measurement-image" />`
            : temperatureImageUrl
            ? `<img src="${temperatureImageUrl}" alt="Outdoor Temperature" class="measurement-image" crossorigin="anonymous" />`
            : '<div class="image-placeholder">No temperature image available</div>'
          }
          <div class="measurement-label">Outdoor Temperature: ${outdoorTemperature}</div>
        </div>

        <div class="measurement-item">
          ${humidityImageDataUrl 
            ? `<img src="${humidityImageDataUrl}" alt="Outdoor Relative Humidity" class="measurement-image" />`
            : humidityImageUrl
            ? `<img src="${humidityImageUrl}" alt="Outdoor Relative Humidity" class="measurement-image" crossorigin="anonymous" />`
            : '<div class="image-placeholder">No humidity image available</div>'
          }
          <div class="measurement-label">Outdoor Relative Humidity: ${outdoorHumidity}</div>
        </div>
      </div>

      <div class="apartment-section">
        ${apartmentImageDataUrl 
          ? `<img src="${apartmentImageDataUrl}" alt="Apartment" class="apartment-image" />`
          : apartmentImageUrl
          ? `<img src="${apartmentImageUrl}" alt="Apartment" class="apartment-image" crossorigin="anonymous" />`
          : '<div class="apartment-placeholder">No apartment image available</div>'
        }
        <div class="apartment-label">Apartment: ${apartmentNum}</div>
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for all images to load
    await waitForImages(tempDiv);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false, // Changed to false to prevent tainted canvas
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: 1123, // A4 height in pixels at 96 DPI (297mm)
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297); // A4 size in mm
  } catch (error) {
    console.error('Error generating measurement images page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates a page with room-specific images (one page per room)
 * @param {jsPDF} doc - The PDF document
 * @param {Object} room - Room data
 * @param {Object} data - Mold inspection data
 * @param {Object} buildingData - Building data
 * @param {string} apartmentNumber - Apartment number
 */
const createRoomImagesPage = async (doc, room, data, buildingData, apartmentNumber, logoDataUrl = '', ticketData = null) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';

  // Get room name
  const roomName = room.room_name || room.name || 'Room';

  // Get image URLs
  const workAreaImageUrl = room.work_area_image || room.room_image || '';
  const temperatureImageUrl = room.temperature_image || '';
  const humidityImageUrl = room.humidity_image || '';
  const moldImageUrl = room.mold_images && room.mold_images.length > 0 
    ? room.mold_images[0] 
    : '';

  // Get dynamic values
  const roomTemperature = room.temperature 
    ? `${parseFloat(room.temperature).toFixed(1)}`
    : 'N/A';
  
  const roomHumidity = room.humidity 
    ? `${parseFloat(room.humidity).toFixed(1)}%`
    : 'N/A';

  // Build full URLs
  const fullWorkAreaUrl = workAreaImageUrl 
    ? (workAreaImageUrl.startsWith('http') ? workAreaImageUrl : `${baseUrl}${workAreaImageUrl}`)
    : '';
  const fullTemperatureUrl = temperatureImageUrl 
    ? (temperatureImageUrl.startsWith('http') ? temperatureImageUrl : `${baseUrl}${temperatureImageUrl}`)
    : '';
  const fullHumidityUrl = humidityImageUrl 
    ? (humidityImageUrl.startsWith('http') ? humidityImageUrl : `${baseUrl}${humidityImageUrl}`)
    : '';
  const fullMoldUrl = moldImageUrl 
    ? (typeof moldImageUrl === 'string' 
        ? (moldImageUrl.startsWith('http') ? moldImageUrl : `${baseUrl}${moldImageUrl}`)
        : (moldImageUrl.url ? `${baseUrl}${moldImageUrl.url}` : ''))
    : '';

  // Convert all images to data URLs to avoid CORS
  let workAreaImageDataUrl = '';
  let temperatureImageDataUrl = '';
  let humidityImageDataUrl = '';
  let moldImageDataUrl = '';

  if (fullWorkAreaUrl) {
    try {
      workAreaImageDataUrl = await convertImageToDataURL(fullWorkAreaUrl);
      if (!workAreaImageDataUrl) {
        workAreaImageDataUrl = fullWorkAreaUrl;
      }
    } catch (error) {
      console.warn('Failed to convert work area image to data URL:', error);
      workAreaImageDataUrl = fullWorkAreaUrl;
    }
  }

  if (fullTemperatureUrl) {
    try {
      temperatureImageDataUrl = await convertImageToDataURL(fullTemperatureUrl);
      if (!temperatureImageDataUrl) {
        temperatureImageDataUrl = fullTemperatureUrl;
      }
    } catch (error) {
      console.warn('Failed to convert temperature image to data URL:', error);
      temperatureImageDataUrl = fullTemperatureUrl;
    }
  }

  if (fullHumidityUrl) {
    try {
      humidityImageDataUrl = await convertImageToDataURL(fullHumidityUrl);
      if (!humidityImageDataUrl) {
        humidityImageDataUrl = fullHumidityUrl;
      }
    } catch (error) {
      console.warn('Failed to convert humidity image to data URL:', error);
      humidityImageDataUrl = fullHumidityUrl;
    }
  }

  if (fullMoldUrl) {
    try {
      moldImageDataUrl = await convertImageToDataURL(fullMoldUrl);
      if (!moldImageDataUrl) {
        moldImageDataUrl = fullMoldUrl;
      }
    } catch (error) {
      console.warn('Failed to convert mold image to data URL:', error);
      moldImageDataUrl = fullMoldUrl;
    }
  }

  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
        padding: 54px 72px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 10px;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .logo {
          width: 50px;
          height: 50px;
          background: #2d8659;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2d8659;
        }
        .company-subtitle {
          font-size: 10px;
          color: #666;
        }
        .contact-info {
          text-align: right;
          font-size: 9px;
          line-height: 1.4;
          color: #333;
        }
        .contact-info div {
          margin-bottom: 2px;
        }
        .room-title {
          text-align: center;
          font-size: 14px;
          font-weight: bold;
          margin: 20px 0;
          color: #000;
        }
        .images-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 15px;
          margin: 20px 0;
        }
        .image-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .image-container {
          width: 60%;
          height: auto;
          border: 1px solid #ddd;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #f9f9f9;
        }
        .room-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 11px;
          background: #f5f5f5;
        }
        .image-label {
          font-size: 11px;
          font-weight: bold;
          color: #000;
          margin-top: 5px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="room-title">${roomName}</div>

      <div class="images-grid">

        <div class="image-item">
          <div class="image-container">
            ${temperatureImageDataUrl 
              ? `<img src="${temperatureImageDataUrl}" alt="${roomName} Temperature" class="room-image" />`
              : fullTemperatureUrl
              ? `<img src="${fullTemperatureUrl}" alt="${roomName} Temperature" class="room-image" crossorigin="anonymous" />`
              : '<div class="image-placeholder">No temperature image available</div>'
            }
          </div>
          <div class="image-label">${roomName} Temperature: ${roomTemperature}</div>
        </div>

        <div class="image-item">
          <div class="image-container">
            ${humidityImageDataUrl 
              ? `<img src="${humidityImageDataUrl}" alt="${roomName} Indoor Relative Humidity" class="room-image" />`
              : fullHumidityUrl
              ? `<img src="${fullHumidityUrl}" alt="${roomName} Indoor Relative Humidity" class="room-image" crossorigin="anonymous" />`
              : '<div class="image-placeholder">No humidity image available</div>'
            }
          </div>
          <div class="image-label">${roomName} Indoor Relative Humidity: ${roomHumidity}</div>
        </div>

        <div class="image-item">
          <div class="image-container">
            ${moldImageDataUrl 
              ? `<img src="${moldImageDataUrl}" alt="${roomName}: Mold Growth" class="room-image" />`
              : fullMoldUrl
              ? `<img src="${fullMoldUrl}" alt="${roomName}: Mold Growth" class="room-image" crossorigin="anonymous" />`
              : '<div class="image-placeholder">No mold growth image available</div>'
            }
          </div>
          <div class="image-label">${roomName}: Mold Growth</div>
        </div>
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for all images to load
    await waitForImages(tempDiv);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false, // Changed to false to prevent tainted canvas
      width: 794, // A4 width in pixels at 96 DPI (210mm)
      height: 1123, // A4 height in pixels at 96 DPI (297mm)
      onclone: async (clonedDoc) => {
        // This function is called with the cloned document
        // You can use it to ensure fonts are loaded in the clone
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        // Ensure images in clone are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            // If already loaded, return immediately
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            // If data URL, should load immediately
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            // For regular URLs, set crossOrigin and wait
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve(); // Continue even if image times out
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve(); // Continue even if image fails
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297); // A4 size in mm
  } catch (error) {
    console.error('Error generating room images page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates a dedicated disclaimer page
 * @param {jsPDF} doc - The PDF document
 * @param {string} logoDataUrl - Optional logo data URL
 */
const createDisclaimerPage = async (doc, logoDataUrl = '', ticketData = null) => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          width: 210mm;
          height: 297mm;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container {
          padding: 18px 24px;
        }
           ${getHeaderStyles()}
        .content-section {
          margin-top: 30px;
        }
        .disclaimer-title {
          font-size: 13px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #000;
          text-decoration: underline;
        }
        .disclaimer-text {
          font-size: 11px;
          line-height: 1.8;
          color: #333;
          text-align: justify;
        }
        .disclaimer-text p {
          margin-bottom: 0;
        }
      </style>
    </head>
    <body>
      <div class="document-container">
        ${getHeaderHTML(logoDataUrl,ticketData)}
        <div class="content-section">
          <div class="disclaimer-title">DISCLAIMER:</div>
          <div class="disclaimer-text">
            <p>The Assessor believes the information contained within this assessment report to be correct at the time of printing. The assessor does not accept responsibility for any consequences arising from the use of the information herein. The report is based on matters which were observed or came to the attention of the assessor during the day of the assessment and should not be relied upon as an exhaustive record of all possible risks or hazards that may exist or potential improvement that can be made.</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;

  // Create a temporary div to render the HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  document.body.appendChild(tempDiv);

  try {
    await document.fonts.ready;

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794,
      height: 1123,
      onclone: async (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
      },
    });

    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297);
  } catch (error) {
    console.error('Error generating disclaimer page:', error);
    throw error;
  } finally {
    document.body.removeChild(tempDiv);
  }
};

/**
 * Preloads an image to ensure it's available
 * @param {string} imageUrl - The URL of the image to preload
 * @returns {Promise<void>}
 */
const preloadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        reject(new Error('Image preload timeout'));
      }
    }, 15000);
    
    img.onload = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolve();
      }
    };
    
    img.onerror = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        // Don't reject, just resolve to continue
        resolve();
      }
    };
    
    img.src = imageUrl;
  });
};

/**
 * Creates a page displaying the inspector certificate image
 * @param {jsPDF} doc - The PDF document
 * @param {Object} inspectorCertificate - Inspector certificate object with attachment_url
 * @param {string} logoDataUrl - Data URL for the logo image
 */
const createInspectorCertificatePage = async (doc, inspectorCertificate, logoDataUrl = '', ticketData = null) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  const certificateImageUrl = inspectorCertificate?.attachment_url 
    ? (inspectorCertificate.attachment_url.startsWith('http') 
        ? inspectorCertificate.attachment_url 
        : `${baseUrl}${inspectorCertificate.attachment_url}`)
    : '';

  // Convert image to data URL to avoid CORS
  let certificateImageDataUrl = '';
  if (certificateImageUrl) {
    try {
      certificateImageDataUrl = await convertImageToDataURL(certificateImageUrl);
      if (!certificateImageDataUrl) {
        console.warn('Certificate image conversion failed, using URL directly');
        certificateImageDataUrl = certificateImageUrl;
      } else {
        console.log('Certificate image converted to data URL successfully');
      }
    } catch (error) {
      console.warn('Failed to convert certificate image to data URL:', error);
      certificateImageDataUrl = certificateImageUrl;
    }
  }

  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
          padding: 54px 72px;
        }
        ${getHeaderStyles()}
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 30px 0;
          padding: 10px 0;
          color: #000;
        }
        .image-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 40px 0;
          min-height: 400px;
        }
        .certificate-image {
          max-width: 100%;
          max-height: 600px;
          height: auto;
          width: auto;
          object-fit: contain;
          border: 1px solid #ddd;
        }
        .image-placeholder {
          width: 100%;
          height: 400px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
        INSPECTOR CERTIFICATE
      </div>

      <div class="image-container">
        ${certificateImageDataUrl 
          ? `<img src="${certificateImageDataUrl}" alt="Inspector Certificate" class="certificate-image" />`
          : certificateImageUrl
          ? `<img src="${certificateImageUrl}" alt="Inspector Certificate" class="certificate-image" crossorigin="anonymous" />`
          : '<div class="image-placeholder">No certificate image available</div>'
        }
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for all images to load
    await waitForImages(tempDiv);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794,
      height: 1123,
      onclone: async (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve();
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297);
  } catch (error) {
    console.error('Error generating inspector certificate page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Creates a page displaying the inspector license image
 * @param {jsPDF} doc - The PDF document
 * @param {Object} inspectorLicense - Inspector license object with attachment_url
 * @param {string} logoDataUrl - Data URL for the logo image
 */
const createInspectorLicensePage = async (doc, inspectorLicense, logoDataUrl = '', ticketData = null) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  const licenseImageUrl = inspectorLicense?.attachment_url 
    ? (inspectorLicense.attachment_url.startsWith('http') 
        ? inspectorLicense.attachment_url 
        : `${baseUrl}${inspectorLicense.attachment_url}`)
    : '';

  // Convert image to data URL to avoid CORS
  let licenseImageDataUrl = '';
  if (licenseImageUrl) {
    try {
      licenseImageDataUrl = await convertImageToDataURL(licenseImageUrl);
      if (!licenseImageDataUrl) {
        console.warn('License image conversion failed, using URL directly');
        licenseImageDataUrl = licenseImageUrl;
      } else {
        console.log('License image converted to data URL successfully');
      }
    } catch (error) {
      console.warn('Failed to convert license image to data URL:', error);
      licenseImageDataUrl = licenseImageUrl;
    }
  }

  // Create HTML template
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        ${fontFaces}
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: "Segoe UI Regular", Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          padding: 18px 24px 10px 24px;
          margin: 0;
        }
        .document-container{
          padding: 54px 72px;
        }
        ${getHeaderStyles()}
        .document-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          margin: 30px 0;
          padding: 10px 0;
          color: #000;
        }
        .image-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 40px 0;
          min-height: 400px;
        }
        .license-image {
          max-width: 100%;
          max-height: 600px;
          height: auto;
          width: auto;
          object-fit: contain;
          border: 1px solid #ddd;
        }
        .image-placeholder {
          width: 100%;
          height: 400px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
        }
      </style>
    </head>
    <body>
    <div class="document-container">
      ${getHeaderHTML(logoDataUrl,ticketData)}

      <div class="document-title">
        INSPECTOR LICENSE
      </div>

      <div class="image-container">
        ${licenseImageDataUrl 
          ? `<img src="${licenseImageDataUrl}" alt="Inspector License" class="license-image" />`
          : licenseImageUrl
          ? `<img src="${licenseImageUrl}" alt="Inspector License" class="license-image" crossorigin="anonymous" />`
          : '<div class="image-placeholder">No license image available</div>'
        }
      </div>
    </div>
    </body>
    </html>
  `;

  // Create a temporary div to hold our HTML
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = '210mm';
  tempDiv.style.backgroundColor = '#ffffff';
  tempDiv.innerHTML = htmlContent;

  // Add the div to the document
  document.body.appendChild(tempDiv);

  try {
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for all images to load
    await waitForImages(tempDiv);

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      width: 794,
      height: 1123,
      onclone: async (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = fontFaces;
        clonedDoc.head.appendChild(style);
        
        const clonedImages = clonedDoc.querySelectorAll('img');
        await Promise.all(
          Array.from(clonedImages).map((img) => {
            if (img.complete && img.naturalHeight !== 0) {
              return Promise.resolve();
            }
            
            if (img.src && img.src.startsWith('data:')) {
              return new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 100);
                }
              });
            }
            
            if (!img.crossOrigin && !img.src.startsWith('data:')) {
              img.crossOrigin = 'anonymous';
            }
            
            return new Promise((resolve) => {
              let resolved = false;
              const timeout = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  resolve();
                }
              }, 10000);
              
              img.onload = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
              
              img.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  clearTimeout(timeout);
                  resolve();
                }
              };
            });
          })
        );
      },
    });

    // Add the canvas image to the PDF
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, 210, 297);
  } catch (error) {
    console.error('Error generating inspector license page:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
};

/**
 * Waits for all images in a container to load
 * @param {HTMLElement} container - The container element
 * @returns {Promise<void>}
 */
const waitForImages = (container) => {
  const images = container.querySelectorAll('img');
  if (images.length === 0) {
    return Promise.resolve();
  }
  
  return Promise.all(
    Array.from(images).map((img) => {
      // If image is already loaded (data URL or cached)
      if (img.complete && img.naturalHeight !== 0) {
        return Promise.resolve();
      }
      
      // If src is a data URL, it should load immediately
      if (img.src && img.src.startsWith('data:')) {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve;
            // Data URLs should load instantly, but add a small timeout
            setTimeout(resolve, 100);
          }
        });
      }
      
      // For regular URLs, wait for load
      return new Promise((resolve) => {
        let resolved = false;
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            console.warn('Image load timeout:', img.src);
            resolve(); // Continue even if image times out
          }
        }, 15000);
        
        // Set crossOrigin if not already set and not a data URL
        if (!img.crossOrigin && !img.src.startsWith('data:')) {
          img.crossOrigin = 'anonymous';
        }
        
        img.onload = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            resolve();
          }
        };
        
        img.onerror = (error) => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            console.warn('Image load error:', img.src, error);
            resolve(); // Continue even if image fails
          }
        };
        
        // If image hasn't started loading, trigger it
        if (!img.src || img.src === '') {
          console.warn('Image has no src:', img);
          resolved = true;
          clearTimeout(timeout);
          resolve();
        }
      });
    })
  );
};

/**
 * Finds a safe cut point by analyzing canvas pixels to detect text boundaries
 * Looks for horizontal lines with mostly white pixels (line breaks/spacing)
 * @param {HTMLCanvasElement} canvas - The source canvas
 * @param {number} targetY - Target Y position to find a safe cut point near
 * @param {number} searchRange - How many pixels to search above/below targetY
 * @returns {number} - Safe Y position to cut at (ensuring full lines are included)
 */
const findSafeCutPoint = (canvas, targetY, searchRange = 100) => {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return targetY;

    const startY = Math.max(0, targetY - searchRange);
    const endY = Math.min(canvas.height, targetY + searchRange);
    const height = endY - startY;
    
    if (height <= 0) return targetY;

    // Sample a region of the canvas to analyze
    const imageData = ctx.getImageData(0, startY, canvas.width, height);
    const data = imageData.data;
    const width = imageData.width;

    let bestY = targetY;
    let bestScore = 0;
    const lineHeightPx = 20; // Approximate line height in pixels

    // Check each horizontal line in the search range
    for (let y = 0; y < height; y++) {
      const actualY = startY + y;
      let whitePixelCount = 0;
      let totalSampled = 0;

      // Sample pixels across the width (check every 4th pixel for performance)
      for (let x = 0; x < width; x += 4) {
        totalSampled++;
        const idx = (y * width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        // Check if pixel is white or very light (RGB > 240)
        if (r > 240 && g > 240 && b > 240) {
          whitePixelCount++;
        }
      }

      const whiteRatio = whitePixelCount / totalSampled;
      
      // Check for consecutive white lines (paragraph breaks - best option)
      let consecutiveWhiteLines = 1;
      if (whiteRatio > 0.85) {
        for (let checkY = y + 1; checkY < Math.min(y + 10, height); checkY++) {
          let checkWhiteCount = 0;
          let checkSampled = 0;
          for (let x = 0; x < width; x += 4) {
            checkSampled++;
            const idx = (checkY * width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            if (r > 240 && g > 240 && b > 240) {
              checkWhiteCount++;
            }
          }
          const checkRatio = checkWhiteCount / checkSampled;
          if (checkRatio > 0.85) {
            consecutiveWhiteLines++;
          } else {
            break;
          }
        }
      }

      // Prefer breaks that are:
      // 1. Mostly white (line breaks)
      // 2. Have consecutive white lines (paragraph breaks - best option)
      // 3. Are at or below target (to include full current line)
      // 4. Are closer to target
      const distanceFromTarget = Math.abs(actualY - targetY);
      const distanceScore = 1 - (distanceFromTarget / searchRange);
      const consecutiveBonus = Math.min(consecutiveWhiteLines / 5, 1.5); // Bonus for paragraph breaks
      const belowTargetBonus = actualY >= targetY ? 1.5 : 0.5; // Strongly prefer cutting at or below target
      
        const score = whiteRatio * distanceScore * (1 + consecutiveBonus * 0.5) * belowTargetBonus;

      // Require at least 75% white pixels for a valid break
      if (score > bestScore && whiteRatio > 0.75) {
        bestScore = score;
        // Find where the white space ends (where next line starts)
        // This ensures we cut after a complete line break
        let whiteSpaceEnd = actualY + (consecutiveWhiteLines * 2);
        
        // Look ahead to find where text starts again
        const checkStartY = y + consecutiveWhiteLines;
        const checkEndY = Math.min(checkStartY + lineHeightPx * 2, height);
        
        for (let checkY = checkStartY; checkY < checkEndY; checkY++) {
          let checkWhiteCount = 0;
          let checkSampled = 0;
          for (let x = 0; x < width; x += 4) {
            checkSampled++;
            const idx = (checkY * width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            if (r > 240 && g > 240 && b > 240) {
              checkWhiteCount++;
            }
          }
          const checkRatio = checkWhiteCount / checkSampled;
          if (checkRatio < 0.5) {
            // Found where text starts again - this is a safe cut point
            whiteSpaceEnd = startY + checkY;
            break;
          }
        }
        
        // Add padding to ensure descenders (g, p, q, y) aren't cut off
        const descenderPadding = consecutiveWhiteLines > 2 ? 8 : 15; // Less padding for paragraph breaks
        bestY = Math.min(canvas.height, whiteSpaceEnd + descenderPadding);
      }
    }

    // If no good break found, add padding to targetY to avoid cutting text
    if (bestY === targetY || Math.abs(bestY - targetY) > searchRange * 0.7) {
      const fallbackPadding = Math.round(12);
      bestY = Math.min(canvas.height, Math.max(0, targetY - fallbackPadding));
    }

    return Math.max(0, Math.min(canvas.height, Math.min(bestY, targetY)));
  } catch (error) {
    console.warn('Error finding safe cut point:', error);
    // Fallback: add padding to target
    const fallbackPadding = Math.round(12);
    return Math.min(canvas.height, Math.max(0, targetY - fallbackPadding));
  }
};

/**
 * Adds a canvas to the PDF and slices it across multiple pages if the content exceeds a single page.
 * Uses intelligent text boundary detection to avoid cutting through sentences.
 * @param {jsPDF} doc - The PDF document
 * @param {HTMLCanvasElement} canvas - The rendered canvas of the page content
 * @param {number} [mmWidth=210] - Width of the PDF page in millimeters
 * @param {number} [mmHeight=297] - Height of the PDF page in millimeters
 */
const addCanvasToPDF = (doc, canvas, mmWidth = 210, mmHeight = 297) => {
  if (!canvas || mmWidth <= 0 || mmHeight <= 0) {
    console.warn('Skipping PDF slicing due to invalid canvas or dimensions');
    return;
  }

  const marginMm = 6;
  const pxPerMm = canvas.width / mmWidth;
  const pageHeightPx = Math.max(Math.round((mmHeight - marginMm * 2) * pxPerMm), 1);
  const reservedSpaceMm = 8;
  const reservedSpacePx = Math.round(reservedSpaceMm * pxPerMm);
  const searchRangePx = Math.round(80 * pxPerMm); // Search 80mm above/below for safe cut point
  let offsetY = 0;

  while (offsetY < canvas.height) {
    const remainingHeight = canvas.height - offsetY;
    
    // For the last page, use remaining height
    if (remainingHeight <= pageHeightPx) {
      const sliceHeight = remainingHeight;
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      if (!ctx) {
        console.warn('Unable to acquire 2D context for canvas slice');
        break;
      }

      ctx.drawImage(
        canvas,
        0,
        offsetY,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight
      );

      const imgData = sliceCanvas.toDataURL('image/png');
      const renderedHeightMm = sliceHeight / pxPerMm;
      doc.addImage(imgData, 'PNG', 0, marginMm, mmWidth, renderedHeightMm);
      break;
    }

    // Find target cut point (end of page)
    const targetY = offsetY + pageHeightPx;
    
    // Find a safe cut point near the target (looks for line breaks/spacing)
    const safeCutY = findSafeCutPoint(canvas, targetY, searchRangePx);
    
    // Ensure we don't go backwards or too far forward
    // Allow some flexibility but keep within reasonable bounds
    const minCutY = offsetY + pageHeightPx * 0.7; // Don't cut too early
    const maxCutY = offsetY + pageHeightPx * 1.2; // Don't cut too late
    const safeCutYClamped = Math.min(safeCutY, targetY);
    let actualCutY = Math.max(minCutY, Math.min(safeCutYClamped, maxCutY));
    const whitespaceGap = targetY - actualCutY;
    if (whitespaceGap < reservedSpacePx) {
      actualCutY = Math.max(minCutY, targetY - reservedSpacePx);
    }
    const sliceHeight = actualCutY - offsetY;

    if (sliceHeight <= 0) {
      // Fallback: use target height if safe cut failed
      const fallbackHeight = Math.min(pageHeightPx, remainingHeight);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = fallbackHeight;
      const ctx = sliceCanvas.getContext('2d');
      if (!ctx) break;

      ctx.drawImage(
        canvas,
        0,
        offsetY,
        canvas.width,
        fallbackHeight,
        0,
        0,
        canvas.width,
        fallbackHeight
      );

      const imgData = sliceCanvas.toDataURL('image/png');
      const renderedHeightMm = fallbackHeight / pxPerMm;
      doc.addImage(imgData, 'PNG', 0, marginMm, mmWidth, renderedHeightMm);
      offsetY += fallbackHeight;
    } else {
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      if (!ctx) {
        console.warn('Unable to acquire 2D context for canvas slice');
        break;
      }

      ctx.drawImage(
        canvas,
        0,
        offsetY,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight
      );

      const imgData = sliceCanvas.toDataURL('image/png');
      const renderedHeightMm = sliceHeight / pxPerMm;
      doc.addImage(imgData, 'PNG', 0, marginMm, mmWidth, renderedHeightMm);
      offsetY = actualCutY;
    }

    if (offsetY < canvas.height) {
      doc.addPage();
    }
  }
};

/**
 * Converts an image URL to a data URL (base64) to avoid CORS issues
 * Uses fetch API first, then falls back to Image element
 * @param {string} imageUrl - The URL of the image to convert
 * @returns {Promise<string>} - The data URL of the image
 */
const convertImageToDataURL = (imageUrl) => {
  return new Promise(async (resolve, reject) => {
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        // Don't reject, return empty string so PDF can still generate
        console.warn('Image conversion timeout:', imageUrl);
        resolve('');
      }
    }, 20000);

    try {
      // First try using fetch API (better CORS handling)
      try {
        const response = await fetch(imageUrl, {
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-cache'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const reader = new FileReader();
        
        reader.onloadend = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            resolve(reader.result);
          }
        };
        
        reader.onerror = () => {
          if (!resolved) {
            // If fetch fails, try Image element approach
            tryImageElement(imageUrl, resolve, timeout);
          }
        };
        
        reader.readAsDataURL(blob);
      } catch (fetchError) {
        // If fetch fails (CORS or other), try Image element approach
        if (!resolved) {
          tryImageElement(imageUrl, resolve, timeout);
        }
      }
    } catch (error) {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        // Don't reject, return empty string so PDF can still generate
        console.warn('Image conversion error:', error);
        resolve('');
      }
    }
  });
};

/**
 * Tries to convert image using Image element (fallback method)
 */
const tryImageElement = (imageUrl, resolve, timeout) => {
  // Try with CORS first
  const img = new Image();
  img.crossOrigin = 'anonymous';
  
  let resolved = false;
  const imgTimeout = setTimeout(() => {
    if (!resolved) {
      resolved = true;
      // If CORS fails, try without CORS (for same-origin images)
      tryWithoutCORS(imageUrl, resolve);
    }
  }, 10000);
  
  img.onload = () => {
    if (resolved) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolved = true;
      clearTimeout(imgTimeout);
      if (timeout) clearTimeout(timeout);
      resolve(dataURL);
    } catch (error) {
      if (!resolved) {
        resolved = true;
        clearTimeout(imgTimeout);
        // If CORS fails, try without CORS (for same-origin images)
        tryWithoutCORS(imageUrl, resolve);
      }
    }
  };
  
  img.onerror = () => {
    if (!resolved) {
      resolved = true;
      clearTimeout(imgTimeout);
      // If CORS fails, try without CORS (for same-origin images)
      tryWithoutCORS(imageUrl, resolve);
    }
  };
  
  img.src = imageUrl;
};

/**
 * Tries to load image without CORS (for same-origin images)
 */
const tryWithoutCORS = (imageUrl, resolve) => {
  // Try Image element without CORS (for same-origin images)
  loadImageWithoutCORS(imageUrl, resolve);
};

/**
 * Loads image using Image element without CORS
 */
const loadImageWithoutCORS = (imageUrl, resolve) => {
  const img = new Image();
  
  let resolved = false;
  const timeout = setTimeout(() => {
    if (!resolved) {
      resolved = true;
      // Don't reject, just resolve with empty string so PDF generation continues
      console.warn('Image load timeout (no CORS):', imageUrl);
      resolve('');
    }
  }, 10000);
  
  img.onload = () => {
    if (resolved) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolved = true;
      clearTimeout(timeout);
      resolve(dataURL);
    } catch (error) {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        // Don't reject, just resolve with empty string
        console.warn('Failed to convert image to data URL:', error);
        resolve('');
      }
    }
  };
  
  img.onerror = () => {
    if (!resolved) {
      resolved = true;
      clearTimeout(timeout);
      // Don't reject, just resolve with empty string so PDF generation continues
      console.warn('Failed to load image:', imageUrl);
      resolve('');
    }
  };
  
  img.src = imageUrl;
};

/**
 * Downloads the generated PDF
 * @param {jsPDF} doc - The generated PDF document
 * @param {string} fileName - The file name for the PDF
 */
export const downloadMoldAssessmentPDF = (doc, fileName) => {
  doc.save(fileName);
};

